import {
  alpha,
  createTheme,
  inputLabelClasses,
  outlinedInputClasses,
  switchClasses,
} from '@mui/material'
import { ThemeOptions } from '@mui/material/styles/createTheme'

import { useCompany } from 'utils/company'

export function useDefaultTheme(): ThemeOptions {
  const { data: company } = useCompany()
  const theme = createTheme(company?.theme)

  return {
    typography: {
      fontFamily: '"Inter", sans-serif !important',
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '.MuiPickersDay-root.Mui-focusVisible, .PrivatePickersYear-yearButton.Mui-focusVisible, .MuiPickersYear-yearButton.Mui-focusVisible': {
            outline: `2px solid ${theme.palette.primary.dark}`,
            outlineOffset: '2px',
          },
          '.MuiPickersDay-root.Mui-selected.Mui-focusVisible, .PrivatePickersYear-yearButton.Mui-selected.Mui-focusVisible, .MuiPickersYear-yearButton.Mui-selected.Mui-focusVisible': {
            outline: `2px solid ${theme.palette.common.white}`,
            outlineOffset: '-4px',
            boxShadow: `0 0 0 2px ${theme.palette.primary.dark}`,
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          h1: {
            fontSize: '24px',
            fontWeight: 400,
            [theme.breakpoints.up('sm')]: {
              fontSize: '32px',
            },
          },
          h2: {
            fontSize: '20px',
            fontWeight: 600,
            [theme.breakpoints.up('sm')]: {
              fontSize: '26px',
            },
          },
          h3: {
            fontSize: '17px',
            fontWeight: 600,
            [theme.breakpoints.up('sm')]: {
              fontSize: '20px',
            },
          },
          h4: {
            fontSize: '14px',
            letterSpacing: '0.2px',
            fontWeight: 600,
            textTransform: 'uppercase',
            [theme.breakpoints.up('sm')]: {
              fontSize: '16px',
              letterSpacing: '0.3px',
            },
          },
          h5: {
            fontSize: '14px',
            fontWeight: 600,
            [theme.breakpoints.up('sm')]: {
              fontSize: '16px',
            },
          },
          h6: {
            fontSize: '12px',
            letterSpacing: '0.2px',
            fontWeight: 600,
            textTransform: 'uppercase',
            [theme.breakpoints.up('sm')]: {
              fontSize: '14px',
              letterSpacing: '0.3px',
            },
          },
          subtitle1: {
            fontSize: '12px',
            fontWeight: 600,
            [theme.breakpoints.up('sm')]: {
              fontSize: '14px',
            },
          },
          subtitle2: {
            fontSize: '10px',
            fontWeight: 600,
            [theme.breakpoints.up('sm')]: {
              fontSize: '12px',
            },
          },
          body1: {
            fontSize: '14px',
            [theme.breakpoints.up('sm')]: {
              fontSize: '16px',
            },
          },
          body2: {
            fontSize: '12px',
            [theme.breakpoints.up('sm')]: {
              fontSize: '14px',
            },
          },
          caption: {
            fontSize: '10px',
            [theme.breakpoints.up('sm')]: {
              fontSize: '12px',
            },
          },
          button: {
            fontSize: '16px',
          },
        },
      },
      MuiFormGroup: {
        styleOverrides: {
          root: {
            gap: theme.spacing(2),
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: `${theme.palette.primary.main} !important`,
            },
            [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: theme.palette.primary.dark,
              borderWidth: '2px',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            whiteSpace: 'normal',
            overflow: 'visible',
            textOverflow: 'clip',
          },
          shrink: {
            [`&:not(.Mui-focused) + div .${outlinedInputClasses.notchedOutline} legend`]: {
              maxWidth: '100%',
            },
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            width: '100%',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&.Mui-focusVisible': {
              outline: `2px solid ${theme.palette.primary.dark}`,
              outlineOffset: '-2px',
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            whiteSpace: 'break-spaces',
            wordBreak: 'break-word',
            [`&:not(.${inputLabelClasses.root})`]: {
              display: 'block',
              marginBottom: '8px',
              color: 'inherit',
              fontSize: '14px',
              fontWeight: 600,
            },
          },
        },
      },
      MuiFormControlLabel: {
        defaultProps: {
          labelPlacement: 'start',
        },
        styleOverrides: {
          root: {
            justifyContent: 'space-between',
            marginRight: 0,
            marginLeft: 0,
            borderRadius: '4px',
            border: '1px solid #E0E0E0',
            [theme.breakpoints.down('sm')]: {
              flexBasis: '100%',
              minWidth: '100%',
            },
            flexBasis: `calc(50% - ${theme.spacing(1)})`,
            minWidth: `calc(50% - ${theme.spacing(1)})`,
            [theme.breakpoints.up('md')]: {
              transition: 'border-color 0.3s',
              '&:hover': {
                borderColor: theme.palette.primary.main,
              },
            },
          },
          label: {
            paddingLeft: '16px',
          },
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
            textTransform: 'none',
            boxShadow: 'none',
            '&.Mui-focusVisible': {
              outline: `2px solid ${theme.palette.primary.dark}`,
              outlineOffset: '2px',
              boxShadow:
                'rgb(0 0 0 / 20%) 0px 3px 5px -1px, rgb(0 0 0 / 14%) 0px 6px 10px 0px, rgb(0 0 0 / 12%) 0px 1px 18px 0px',
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: theme.palette.text.secondary,
            borderRadius: '8px',
            '@media (hover)': {
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.dark, 0.1),
                '& :is(p, svg)': {
                  color: theme.palette.primary.dark,
                },
              },
            },
            '&.Mui-focusVisible': {
              outline: `2px solid ${theme.palette.primary.dark}`,
              outlineOffset: '2px',
            },
          },
        },
      },
      MuiSnackbarContent: {
        styleOverrides: {
          message: {
            display: 'flex',
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            paddingTop: '8px',
            paddingBottom: '8px',
            '&.Mui-focusVisible': {
              outline: `2px solid ${theme.palette.primary.dark}`,
              outlineOffset: '2px',
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            width: '32px',
            height: '32px',
            padding: '7px',
            borderRadius: '4px',
            '&.Mui-focusVisible': {
              outline: `2px solid ${theme.palette.primary.dark}`,
              outlineOffset: '2px',
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            marginRight: '-12px',
          },
          switchBase: {
            [`&:not(.${switchClasses.checked})`]: {
              [`.${switchClasses.thumb}`]: {
                color: alpha(theme.palette.primary.light, 0.7),
              },
              [`& + .${switchClasses.track}`]: {
                backgroundColor: alpha(theme.palette.primary.light, 0.7),
              },
            },
          },
        },
      },
      MuiTableCell: {
        defaultProps: {
          padding: 'none',
        },
        styleOverrides: {
          root: {
            borderBottom: 'none',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          label: {
            paddingRight: '10px',
            paddingLeft: '10px',
            '&:first-letter': {
              textTransform: 'uppercase',
            },
          },
        },
      },
    },
  }
}
